import React, { useState, useEffect } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { FileBox, Cloud } from 'lucide-react';
import { DriveFileSyncModal } from '../../modals/data-source/DriveFileSyncModal';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';
import {
    GoogleDriveFile,
    GoogleDrivePickerProps,
    GoogleTokenResponse,
    DriveResponse
} from '../../../types/knowledge-base';

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({
                                                                        onUploadSuccess,
                                                                        clientId,
                                                                        onFilesLoad,
                                                                        onError,
                                                                        onLoadingChange
                                                                    }) => {
    const [showModal, setShowModal] = useState(false);
    const [files, setFiles] = useState<GoogleDriveFile[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nextPageToken, setNextPageToken] = useState<string | undefined>();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [token, setToken] = useState<string | null>(null);

    const loadDriveFiles = async (accessToken: string, pageToken?: string): Promise<void> => {
        if (!accessToken) {
            const errorMessage = 'No access token provided';
            setError(errorMessage);
            onError?.(errorMessage);
            return;
        }

        setIsLoading(true);
        onLoadingChange?.(true);
        setError(null);

        try {
            const url = new URL(API_ENDPOINTS.DRIVE.LIST_FILES);
            if (pageToken) {
                url.searchParams.append('pageToken', pageToken);
            }

            const data = await apiService.get<DriveResponse>(url.toString(), {
                headers: {
                    'drive-auth-token': accessToken,
                    'x-cloud-provider': 'aws'
                }
            });

            const formattedFiles: GoogleDriveFile[] = data.files.map(file => ({
                ...file,
                size: formatFileSize(parseInt(file.size || '0', 10)),
                webViewLink: `https://drive.google.com/file/d/${file.id}/view`
            }));

            const newFiles = pageToken ? [...files, ...formattedFiles] : formattedFiles;
            setFiles(newFiles);
            onFilesLoad?.(newFiles);
            setNextPageToken(data.nextPageToken);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load drive files';
            setError(errorMessage);
            onError?.(errorMessage);
        } finally {
            setIsLoading(false);
            onLoadingChange?.(false);
        }
    };

    const handleAuthClick = async (): Promise<void> => {
        try {
            if (!window.google?.accounts?.oauth2) {
                throw new Error('Google OAuth not initialized');
            }

            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: 'https://www.googleapis.com/auth/drive.readonly',
                callback: (response: GoogleTokenResponse) => {
                    if (response.error) {
                        const errorMessage = `Google OAuth error: ${response.error}`;
                        setError(errorMessage);
                        onError?.(errorMessage);
                        return;
                    }

                    if (response.access_token) {
                        setToken(response.access_token);
                        setIsAuthorized(true);
                        handleBrowseClick(response.access_token);
                    } else {
                        setError('Failed to receive access token');
                        onError?.('Failed to receive access token');
                    }
                },
            });

            tokenClient.requestAccessToken();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to authorize with Google Drive';
            setError(errorMessage);
            onError?.(errorMessage);
        }
    };

    const handleBrowseClick = (newToken?: string): void => {
        setShowModal(true);
        const tokenToUse = newToken || token;
        if (tokenToUse) {
            void loadDriveFiles(tokenToUse);
        } else {
            void handleAuthClick();
        }
    };

    const handleSync = async (fileIds: string[]): Promise<void> => {
        if (!token) {
            const errorMessage = 'Not authenticated with Google Drive';
            setError(errorMessage);
            onError?.(errorMessage);
            return;
        }

        try {
            const response = await apiService.post<{ success: boolean }>(
                API_ENDPOINTS.DRIVE.SYNC_FILES,
                {
                    fileIds,
                    cloudProvider: 'aws'
                },
                {
                    headers: {
                        'drive-auth-token': token,
                        'x-cloud-provider': 'aws'
                    }
                }
            );

            if (response.success) {
                onUploadSuccess();
                setShowModal(false);
            } else {
                throw new Error('Sync operation failed');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to sync files';
            setError(errorMessage);
            onError?.(errorMessage, fileIds);
            throw err;
        }
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.google?.accounts?.oauth2) {
                window.google.accounts.oauth2.initTokenClient({
                    client_id: clientId,
                    scope: 'https://www.googleapis.com/auth/drive.readonly',
                    callback: (response: GoogleTokenResponse) => {
                        if (response.access_token) {
                            setToken(response.access_token);
                            setIsAuthorized(true);
                        }
                    },
                });
            }
        };

        document.body.appendChild(script);

        return () => {
            const scriptElement = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (scriptElement) {
                document.body.removeChild(scriptElement);
            }
        };
    }, [clientId]);

    return (
        <>
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Body className="p-4">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="text-primary mb-3">
                            <Cloud size={48} />
                        </div>
                        <h5 className="mb-2">Import from Google Drive</h5>
                        <p className="text-muted text-center mb-3">
                            Select and sync files directly from your Google Drive
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => isAuthorized ? handleBrowseClick() : void handleAuthClick()}
                            disabled={isLoading}
                            className="d-flex align-items-center gap-2"
                        >
                            <FileBox size={20} />
                            {isLoading ? 'Loading...' : isAuthorized ? 'Browse Drive Files' : 'Connect Google Drive'}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {isAuthorized && (
                <DriveFileSyncModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onSync={handleSync}
                    files={files}
                    isLoading={isLoading}
                    error={error}
                    onRefresh={() => token && void loadDriveFiles(token)}
                    onLoadMore={nextPageToken ? () => token && void loadDriveFiles(token, nextPageToken) : undefined}
                    hasMore={!!nextPageToken}
                />
            )}
        </>
    );
};

export default GoogleDrivePicker;